/**
 * Compact voting times display for election header.
 * Shows date range + "View voting times" button that expands detailed schedule inline.
 *
 * Props:
 *   earlyVotingStart: YYYY-MM-DD (derived by API from canonical schedule windows)
 *   electionDay: YYYY-MM-DD (election day date)
 *   scheduleWindows: canonical array from ElectionScheduleWindow
 */
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import { Button } from '../../../components/ui';
import { formatVotingDateRange, normalizeScheduleWindows } from '../utils/votingScheduleFormatter';
import { ElectionWeekCalendar } from './ElectionWeekCalendar';

export default function ElectionVotingTimes({ electionDay, earlyVotingStart, scheduleWindows }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const normalizedWindows = useMemo(
    () => normalizeScheduleWindows(scheduleWindows),
    [scheduleWindows]
  );

  if (!normalizedWindows.length) {
    return null;
  }

  const effectiveStartDate = earlyVotingStart || normalizedWindows[0]?.dateStart;
  const effectiveEndDate = electionDay || normalizedWindows[normalizedWindows.length - 1]?.dateEnd;
  const dateRangeLabel = formatVotingDateRange(effectiveStartDate, effectiveEndDate);

  return (
    <div className="mt-4 border-t border-stone-200 pt-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-lg font-semibold text-stone-900">{dateRangeLabel}</p>
          <p className="mt-1 text-sm text-stone-600">
            View voting hours in a weekly calendar layout.
          </p>
        </div>

        <Button type="button" onClick={() => setIsExpanded((current) => !current)}>
          {isExpanded ? 'Hide voting times' : 'View voting times'}
        </Button>
      </div>

      {isExpanded ? (
        <div className="mt-5">
          <ElectionWeekCalendar scheduleWindows={normalizedWindows} />
        </div>
      ) : null}
    </div>
  );
}

ElectionVotingTimes.propTypes = {
  electionDay: PropTypes.string,
  earlyVotingStart: PropTypes.string,
  scheduleWindows: PropTypes.arrayOf(PropTypes.object),
};

ElectionVotingTimes.defaultProps = {
  electionDay: null,
  earlyVotingStart: null,
  scheduleWindows: [],
};
